import type { z } from "zod";
import { ApiErrorBody } from "./schemas";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly fields: Record<string, string> = {},
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** The response didn't match the schema we assumed — a contract drift, not a user error. */
export class ContractError extends Error {
  constructor(
    readonly path: string,
    readonly issues: z.core.$ZodIssue[],
  ) {
    super(`Unexpected response shape from ${path}`);
    this.name = "ContractError";
  }
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
  /** Bearer token (region API). Control-plane calls use the `atx_session` cookie instead. */
  token?: string;
  signal?: AbortSignal;
}

export async function request<S extends z.ZodType>(
  schema: S,
  url: string,
  { method = "GET", body, token, signal }: RequestOptions = {},
): Promise<z.infer<S>> {
  const headers: Record<string, string> = { accept: "application/json" };
  if (body !== undefined) headers["content-type"] = "application/json";
  if (token) headers.authorization = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(url, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: token ? "omit" : "include",
      signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    throw new ApiError(0, "network", "Can't reach Atrix Analytics. Check your connection and try again.");
  }

  const text = await res.text();
  const json: unknown = text ? safeJson(text) : undefined;

  if (!res.ok) {
    const parsed = ApiErrorBody.safeParse(json);
    if (parsed.success) {
      const { code, message } = parsed.data.error;
      throw new ApiError(res.status, code, humanise(message), fieldsFor(code, message));
    }
    throw new ApiError(res.status, `http_${res.status}`, fallbackMessage(res.status));
  }

  const parsed = schema.safeParse(json);
  if (!parsed.success) throw new ContractError(url, parsed.error.issues);
  return parsed.data;
}

/** Sentence-case a server message ("email is not a valid address" → "Email is not a valid address."). */
export function humanise(message: string): string {
  const m = message.trim();
  if (!m) return m;
  const s = m.charAt(0).toUpperCase() + m.slice(1);
  return /[.!?]$/.test(s) ? s : `${s}.`;
}

/** Form fields the console highlights, by the name the API uses at the start of a validation message. */
const KNOWN_FIELDS = ["email", "password", "name", "timezone", "currency", "region", "scopes", "expires_in_days"];

/**
 * The control plane reports one error per request, `{code, message}` — no field map. Map it onto the
 * form field it concerns: codes that name a field outright, else a `validation` message that starts
 * with the field name ("timezone must be an IANA zone name…").
 */
export function fieldsFor(code: string, message: string): Record<string, string> {
  const msg = humanise(message);
  switch (code) {
    case "email_taken":
      return { email: "An account with this email already exists — sign in instead." };
    case "weak_password":
      return { password: msg };
    case "region_unavailable":
    case "unknown_region":
      return { region: msg };
    case "unknown_scope":
    case "scope_not_held":
      return { scopes: msg };
    case "validation": {
      const first = message.trim().split(/[\s:`]/, 1)[0] ?? "";
      return KNOWN_FIELDS.includes(first) ? { [first]: msg } : {};
    }
    default:
      return {};
  }
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

function fallbackMessage(status: number): string {
  if (status === 401) return "Your session has ended. Sign in again.";
  if (status === 404) return "Not found.";
  if (status === 429) return "Too many requests — wait a moment and try again.";
  if (status >= 500) return "Atrix Analytics had a problem handling that. Try again shortly.";
  return "That request couldn't be completed.";
}

/** A human message for any thrown value. */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof ContractError) return "The server sent a response this console doesn't understand yet.";
  if (err instanceof Error) return err.message;
  return "Something went wrong.";
}
